<template>
  <v-select
    v-bind="$attrs"
    :model-value="modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
    :items="items"
    :item-title="itemTitle"
    :item-value="itemValue"
    multiple
    variant="outlined"
    hide-details
    class="w-100 mb-4 closd-select"
    clearable
    density="compact"
    :placeholder="placeholder"
    :menu-props="{ maxWidth: '100%' }"
  >
    <template #item="{ props, item }">
      <div v-bind="{ ...props, title: undefined }" class="custom-option mx-2">
          <v-checkbox
            :model-value="(modelValue || []).includes(resolveItemValue(item.raw))"
            class="d-flex align-center justify-center"
          />
        <span class="text-text-secondary">{{ resolveItemTitle(item.raw) }}</span>
      </div>
    </template>

    <template #no-data>
      <slot name="no-data"/>
    </template>
  </v-select>
</template>

<script setup lang="ts">

const props = defineProps<{
  modelValue: any[]
  items: any[]
  itemTitle?: string | ((item: any) => string)
  itemValue?: string | ((item: any) => any)
  placeholder?: string
}>()

const emit = defineEmits<(e: 'update:modelValue', value: any[]) => void>()

function resolveItemTitle(item: any): string {
  if (typeof props.itemTitle === 'function') return props.itemTitle(item)
  return item?.[props.itemTitle || 'label']
}

function resolveItemValue(item: any): any {
  if (typeof props.itemValue === 'function') return props.itemValue(item)
  return item?.[props.itemValue || 'value']
}
</script>

<style scoped lang="scss">
.custom-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  min-height: 40px !important;
  height: 40px !important;
  &:hover {
    background-color: #f5f5f5;
  }
}
:deep(.v-field__input) {
  opacity: 1 !important;
}
:deep(.v-field__input input) {
  margin-top:2px;
  &::placeholder {
    color: rgb(var(--v-theme-grey-500)) !important;
    opacity: 1 !important;
  }
}

.closd-select {
  :deep(.v-field) {
    --outline-color: rgb(var(--v-theme-border-secondary));
    --outline-width: 1px;
    --text-color: rgb(var(--v-theme-text-secondary));
    --background-color: rgb(var(--v-theme-surface-primary));
    --v-disabled-opacity: 1;
    --placeholder-color: rgb(var(--v-theme-text-placeholder));
    color: var(--text-color);
    background-color: var(--background-color);

    &:not(.v-field--disabled):hover {
      --outline-color: rgb(var(--v-theme-border-secondary-hover));
    }

    &:not(.v-field--disabled):focus-within {
      --outline-color: rgb(var(--v-theme-border-info-bold));
      --outline-width: 2px;
    }

    &.v-field--disabled {
      --text-color: rgb(var(--v-theme-text-disabled));
      --background-color: rgb(var(--v-theme-surface-disabled));
      --outline-color: rgb(var(--v-theme-border-disabled));
      --placeholder-color: rgb(var(--v-theme-text-disabled));
    }

    &.v-field--error {
      --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
    }
  }

  :deep(.v-field__input) {
    opacity: 1;
  }

  :deep(.v-field__input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end), {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field--active .v-field__outline__notch::before) {
    opacity: 0;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  :deep(.v-field__append-inner i) {
    color: rgb(var(--v-theme-icon-secondary));
  }

  :deep(.v-label) {
    color: rgb(var(--v-theme-text-primary));
  }
}
</style>
